import React, { useState, useEffect, useRef, useCallback } from "react";
import LoginPagePNG from "../assets/images/loginPage.png";
import LogoSVG from "../assets/icons/logo.svg";
import { ForgotPasswordModal } from "../components/modals/ForgotPasswordModal";
import { TwoFactorSetupModal } from "../components/modals/TwoFactorSetupModal";
import { TwoFactorVerifyModal } from "../components/modals/TwoFactorVerifyModal";
import { ChangePasswordModal } from "../components/modals/ChangePasswordModal";
import { ResetPasswordModal } from "../components/modals/ResetPasswordModal";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/authService";

interface LoginScreenProps {
  forgotPasswordKey?: string;
}

const LoginPage: React.FC<LoginScreenProps> = ({ forgotPasswordKey }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [displayError, setDisplayError] = useState<string>("");
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [show2FAVerifyModal, setShow2FAVerifyModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string>("");
  const [twoFASetupError, setTwoFASetupError] = useState<string>("");
  const [twoFAVerifyError, setTwoFAVerifyError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const { authenticate, authenticateGoogle, commitSession } = useAuth();

  // Holds an authenticated-but-not-yet-activated user while we decide whether to
  // prompt 2FA setup, and the Google credential when verifying a 2FA-enabled user.
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState<string | null>(null);

  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (forgotPasswordKey) {
      setShowResetPasswordModal(true);
    }
  }, [forgotPasswordKey]);

  const isTwoFactorRequired = (err: any) =>
    err?.response?.status === 401 && err?.response?.data?.detail?.message === "2FA code is required";

  // After a successful authentication: a 2FA-enabled user is already verified and
  // is activated; a user WITHOUT 2FA is offered the (skippable) setup popup.
  const proceedAfterAuth = useCallback(async (u: any) => {
    if (u?.is_2fa_enabled) {
      commitSession(u);
      return;
    }
    try {
      const setup = await authService.setup2FA();
      setQrCodeData(setup.qr_code);
    } catch {
      setQrCodeData("");
    }
    setPendingUser(u);
    setShow2FASetupModal(true);
  }, [commitSession]);

  const handleGoogleCredential = useCallback(async (response: { credential?: string }) => {
    if (!response?.credential) return;
    setIsLoading(true);
    setDisplayError("");
    try {
      const u = await authenticateGoogle(response.credential);
      await proceedAfterAuth(u);
    } catch (err: any) {
      if (isTwoFactorRequired(err)) {
        setPendingGoogleCredential(response.credential!);
        setShow2FAVerifyModal(true);
        return;
      }
      const detail = err.response?.data?.detail;
      setDisplayError(typeof detail === "string" ? detail : "Google login failed");
    } finally {
      setIsLoading(false);
    }
  }, [authenticateGoogle, proceedAfterAuth]);

  // Load Google Identity Services and render the official Sign-in button.
  useEffect(() => {
    if (!googleClientId) return;
    const SCRIPT_SRC = "https://accounts.google.com/gsi/client";
    const init = () => {
      const g = (window as any).google;
      if (!g?.accounts?.id || !googleButtonRef.current) return;
      g.accounts.id.initialize({ client_id: googleClientId, callback: handleGoogleCredential });
      googleButtonRef.current.innerHTML = "";
      g.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline", size: "large", width: 320, text: "signin_with",
      });
    };
    if ((window as any).google?.accounts?.id) { init(); return; }
    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    let created = false;
    if (!script) {
      script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      created = true;
    }
    script.addEventListener("load", init);
    return () => { script?.removeEventListener("load", init); if (created) { /* keep script cached */ } };
  }, [googleClientId, handleGoogleCredential]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev: any) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      const u = await authenticate(formData.email, formData.password);
      await proceedAfterAuth(u);
    } catch (err: any) {
      console.error("Login error:", err);

      if (isTwoFactorRequired(err)) {
        setPendingGoogleCredential(null);
        setShow2FAVerifyModal(true);
        setDisplayError("");
        return;
      }

      if (err.response?.status === 401 && err.response?.data?.detail === "Password Expired") {
        setShowChangePasswordModal(true);
        setDisplayError("");
        return;
      }

      const detail = err.response?.data?.detail;
      setDisplayError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Enable 2FA from the setup popup, then activate the session.
  const handle2FAEnable = async (token: string) => {
    setTwoFASetupError("");
    try {
      await authService.enable2FA(token);
      const u = await authService.getCurrentUser();
      setShow2FASetupModal(false);
      setQrCodeData("");
      commitSession(u);
    } catch (err: any) {
      console.error("[LoginPage] Failed to enable 2FA:", err);
      // Show the error inside the 2FA popup (not on the login page behind it).
      setTwoFASetupError("Failed to enable 2FA. Please verify the code and try again.");
    }
  };

  // Skip 2FA setup and continue into the app.
  const handle2FASkip = () => {
    setShow2FASetupModal(false);
    setQrCodeData("");
    setTwoFASetupError("");
    if (pendingUser) commitSession(pendingUser);
  };

  // Verify a 2FA code for a 2FA-enabled user (password or Google), then activate.
  const handle2FAVerify = async (token: string) => {
    setTwoFAVerifyError("");
    try {
      const u = pendingGoogleCredential
        ? await authenticateGoogle(pendingGoogleCredential, token)
        : await authenticate(formData.email, formData.password, token);
      setShow2FAVerifyModal(false);
      setPendingGoogleCredential(null);
      commitSession(u);
    } catch (err: any) {
      console.error("[LoginPage] Failed to verify 2FA:", err);
      // Show the error inside the 2FA popup (not on the login page behind it).
      setTwoFAVerifyError("Invalid 2FA code. Please try again.");
    }
  };

  // Cancel the setup popup → abort the login (discard the issued tokens).
  const handle2FACancel = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setShow2FASetupModal(false);
    setQrCodeData("");
    setTwoFASetupError("");
    setPendingUser(null);
    setPendingGoogleCredential(null);
  };

  const handlePasswordChanged = () => {
    console.log("[LoginPage] Password changed successfully");
    setShowChangePasswordModal(false);
    setDisplayError("Password changed successfully. Please login with your new password.");
  };

  const handlePasswordReset = () => {
    console.log("[LoginPage] Password reset successfully");
    setShowResetPasswordModal(false);
    setDisplayError("Password reset successfully. Please login with your new password.");
    window.history.pushState({}, '', '/');
  };

  return (
    <>
      <div className="flex flex-wrap m-0 w-full min-h-screen bg-brand-green">
        <div className="hidden sm:block sm:w-5/12 lg:w-1/3 p-0 relative">
          <img
            src={LoginPagePNG}
            alt="Login page design"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="w-full sm:w-7/12 lg:w-2/3 flex flex-col">
          <div className="my-auto">
            <div className="w-full sm:w-8/12 lg:w-5/12 mx-auto px-4">
              <div className="text-center mb-10">
                <img src={LogoSVG} alt="PREDICTIX CARE" className="h-20 mx-auto" />
              </div>

              <div className="text-brand-darkTeal text-base font-semibold mb-6">
                Let's get started
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-5">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                    className="w-full px-5 py-3 rounded-[20px] border border-gray-300 bg-[#eef2f7] text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                  />
                  {formErrors.email && (
                    <div className="text-red-600 text-xs mt-1">
                      {formErrors.email}
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Password"
                    className="w-full px-5 py-3 rounded-[20px] border border-gray-300 bg-[#eef2f7] text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                  />
                  {formErrors.password && (
                    <div className="text-red-600 text-xs mt-1">
                      {formErrors.password}
                    </div>
                  )}
                </div>

                <div className="ps-3 mb-5">
                  <button
                    type="button"
                    onClick={() => setShowForgotPasswordModal(true)}
                    className="bg-transparent border-none text-brand-teal text-sm font-normal cursor-pointer p-0 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>

                {displayError && (
                  <div className={`p-3 rounded-lg mb-5 text-sm ${
                    displayError.toLowerCase().includes('successfully')
                      ? 'bg-green-50 border border-green-300 text-green-800'
                      : 'bg-red-50 border border-red-300 text-red-800'
                  }`}>
                    {!displayError.toLowerCase().includes('successfully') && <strong>Login Error:</strong>} {displayError}
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full px-5 py-3 border-none rounded-[20px] text-sm font-semibold bg-brand-orange text-white hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed transition-opacity"
                  >
                    {isLoading ? "Logging in..." : "Login"}
                  </button>

                  {googleClientId && (
                    <div ref={googleButtonRef} className="w-full flex justify-center" />
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />

      <TwoFactorSetupModal
        isOpen={show2FASetupModal}
        onClose={handle2FACancel}
        qrCode={qrCodeData}
        onEnable={handle2FAEnable}
        onSkip={handle2FASkip}
        error={twoFASetupError}
      />

      <TwoFactorVerifyModal
        isOpen={show2FAVerifyModal}
        onClose={() => {
          setShow2FAVerifyModal(false);
          setPendingGoogleCredential(null);
          setTwoFAVerifyError("");
        }}
        onVerify={handle2FAVerify}
        error={twoFAVerifyError}
      />

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        email={formData.email}
        onPasswordChanged={handlePasswordChanged}
      />

      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        resetKey={forgotPasswordKey || ""}
        onPasswordReset={handlePasswordReset}
      />
    </>
  );
};

export default LoginPage;
